import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type {
  CreateMiceProjectRequest,
  MiceClientOption,
  MiceDebitCompanyOptions,
  MiceCorporatePath,
  MiceNamedRef,
  MiceRequesterOption,
  MiceWorkspaceOptions,
} from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { miceRepository } from '@web/data/repositories';
import { useOrgContext } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';

type Ref = MiceNamedRef | null;

export interface MiceProjectFormState {
  name: string;
  setName: (value: string) => void;
  eventStart: string;
  setEventStart: (value: string) => void;
  eventEnd: string;
  setEventEnd: (value: string) => void;
  /** Fim antes do início (só com as duas datas preenchidas). */
  rangeInvalid: boolean;
  /** Nome e período preenchidos e coerentes: a etapa 2 do cadastro pode abrir. */
  eventComplete: boolean;
  /** Busca de cliente (grupo corporativo) e de solicitante (no workspace escolhido). */
  clientSearch: SearchSource<MiceClientOption>;
  requesterSearch: SearchSource<MiceRequesterOption>;
  client: MiceClientOption | null;
  setClient: (value: MiceClientOption | null) => void;
  workspace: Ref;
  setWorkspace: (value: Ref) => void;
  /** Empresas de débito e motivos do workspace; `undefined` até ele ser escolhido e carregar. */
  workspaceOptions: MiceWorkspaceOptions | undefined;
  debitCompany: Ref;
  setDebitCompany: (value: Ref) => void;
  /** Centros de custo e projetos da empresa de débito escolhida. */
  debitCompanyOptions: MiceDebitCompanyOptions | undefined;
  optionsFailed: boolean;
  costCenter: Ref;
  setCostCenter: (value: Ref) => void;
  /** Caminho do centro de custo; `event` some quando a empresa de débito não tem nenhum. */
  costCenterPath: MiceCorporatePath;
  setCostCenterPath: (value: MiceCorporatePath) => void;
  costCenterEventAllowed: boolean;
  motive: Ref;
  setMotive: (value: Ref) => void;
  motivePath: MiceCorporatePath;
  setMotivePath: (value: MiceCorporatePath) => void;
  motiveEventAllowed: boolean;
  corporateProject: Ref;
  setCorporateProject: (value: Ref) => void;
  requester: MiceRequesterOption | null;
  setRequester: (value: MiceRequesterOption | null) => void;
  canSubmit: boolean;
  submit: () => void;
}

interface SearchSource<T> {
  queryKey: (term: string) => QueryKey;
  search: (term: string) => Promise<T[]>;
}

interface Corporate {
  client: MiceClientOption;
  workspace: MiceNamedRef;
  debitCompany: MiceNamedRef;
  /** Só no caminho `event`; nos demais o valor fica com o viajante. */
  costCenter: Ref;
  motive: Ref;
  costCenterPath: MiceCorporatePath;
  motivePath: MiceCorporatePath;
  requester: MiceRequesterOption;
}

interface EventFields {
  name: string;
  eventStart: string;
  eventEnd: string;
}

/** Fim antes do início, com as duas datas preenchidas (ISO compara como texto). */
const isRangeInvalid = (e: EventFields): boolean =>
  e.eventStart !== '' && e.eventEnd !== '' && e.eventEnd < e.eventStart;

const isEventComplete = (e: EventFields): boolean =>
  e.name.trim() !== '' && e.eventStart !== '' && e.eventEnd !== '' && !isRangeInvalid(e);

type Nullable<T> = { [K in keyof T]: T[K] | null };

type CorporateInput = Nullable<Omit<Corporate, 'costCenterPath' | 'motivePath'>> &
  Pick<Corporate, 'costCenterPath' | 'motivePath'>;

/** O valor só é exigido no caminho `event`. */
function corporateOf(refs: CorporateInput): Corporate | null {
  const { client, workspace, debitCompany, costCenter, motive, requester } = refs;
  if (!client || !workspace || !debitCompany || !requester) return null;
  if (refs.costCenterPath === 'event' && !costCenter) return null;
  if (refs.motivePath === 'event' && !motive) return null;
  return { ...refs, client, workspace, debitCompany, requester };
}

function toCreateRequest(
  event: EventFields,
  refs: Corporate,
  corporateProject: Ref,
): CreateMiceProjectRequest {
  return {
    name: event.name.trim(),
    eventStart: event.eventStart,
    eventEnd: event.eventEnd,
    clientId: refs.client.id,
    workspaceId: refs.workspace.id,
    debitCompanyId: refs.debitCompany.id,
    costCenterPath: refs.costCenterPath,
    motivePath: refs.motivePath,
    ...(refs.costCenterPath === 'event' && refs.costCenter
      ? { costCenterId: refs.costCenter.id }
      : {}),
    ...(refs.motivePath === 'event' && refs.motive ? { motiveId: refs.motive.id } : {}),
    ...(corporateProject ? { corporateProjectId: corporateProject.id } : {}),
    requester: {
      id: refs.requester.id,
      email: refs.requester.email,
      origin: refs.requester.origin,
    },
  };
}

/** O caminho escolhido; sem escolha (ou com `event` indisponível), o padrão do campo. */
function pathOf(chosen: MiceCorporatePath | null, eventAllowed: boolean): MiceCorporatePath {
  if (chosen && (chosen !== 'event' || eventAllowed)) return chosen;
  return eventAllowed ? 'event' : 'traveler_rules';
}

type Chosen = Pick<MiceProjectFormState, 'debitCompany' | 'costCenter' | 'motive'>;

/** A única opção da lista, que não deixa o que escolher; `null` se há zero ou várias. */
const onlyOption = (items: readonly MiceNamedRef[] | undefined): Ref =>
  items?.length === 1 ? (items[0] ?? null) : null;

/**
 * As listas que dependem do workspace e da empresa de débito escolhidos. Empresa de débito,
 * motivo e centro de custo que vêm sozinhos na lista valem como escolhidos; o que o usuário
 * escolheu à mão sempre prevalece.
 */
function useCorporateOptions(
  orgId: string,
  clientId: string,
  workspace: Ref,
  chosen: Chosen,
): Pick<
  MiceProjectFormState,
  | 'workspaceOptions'
  | 'debitCompanyOptions'
  | 'optionsFailed'
  | 'debitCompany'
  | 'costCenter'
  | 'motive'
> {
  const workspaceId = workspace?.id ?? '';
  const workspaceQuery = useQuery({
    queryKey: queryKeys.mice.workspaceOptions(orgId, clientId, workspaceId),
    queryFn: () => miceRepository.workspaceOptions(orgId, workspaceId, clientId),
    enabled: workspace !== null,
    meta: { silent: true },
  });
  const workspaceOptions = workspace ? workspaceQuery.data : undefined;
  const debitCompany = chosen.debitCompany ?? onlyOption(workspaceOptions?.debitCompanies);
  const debitCompanyId = debitCompany?.id ?? '';
  const debitCompanyQuery = useQuery({
    queryKey: queryKeys.mice.debitCompanyOptions(orgId, clientId, workspaceId, debitCompanyId),
    queryFn: () => miceRepository.debitCompanyOptions(orgId, workspaceId, clientId, debitCompanyId),
    enabled: workspace !== null && debitCompany !== null,
    meta: { silent: true },
  });
  const debitCompanyOptions = debitCompany ? debitCompanyQuery.data : undefined;
  return {
    workspaceOptions,
    debitCompanyOptions,
    optionsFailed: workspaceQuery.isError || debitCompanyQuery.isError,
    debitCompany,
    costCenter: chosen.costCenter ?? onlyOption(debitCompanyOptions?.costCenters),
    motive: chosen.motive ?? onlyOption(workspaceOptions?.motives),
  };
}

export function useMiceProjectForm(): MiceProjectFormState {
  const { orgId, orgSlug } = useOrgContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [eventStart, setEventStart] = useState('');
  const [eventEnd, setEventEnd] = useState('');
  const [client, setClient] = useState<MiceClientOption | null>(null);
  const [workspace, setWorkspace] = useState<Ref>(null);
  const [chosenDebitCompany, setChosenDebitCompany] = useState<Ref>(null);
  const [chosenCostCenter, setChosenCostCenter] = useState<Ref>(null);
  const [chosenMotive, setChosenMotive] = useState<Ref>(null);
  const [chosenCostCenterPath, setChosenCostCenterPath] = useState<MiceCorporatePath | null>(null);
  const [chosenMotivePath, setChosenMotivePath] = useState<MiceCorporatePath | null>(null);
  const [corporateProject, setCorporateProject] = useState<Ref>(null);
  const [requester, setRequester] = useState<MiceRequesterOption | null>(null);

  const workspaceId = workspace?.id ?? '';
  const clientId = client?.id ?? '';
  const options = useCorporateOptions(orgId, clientId, workspace, {
    debitCompany: chosenDebitCompany,
    costCenter: chosenCostCenter,
    motive: chosenMotive,
  });
  const { debitCompany, costCenter, motive } = options;
  // O caminho começa em `event`; com a lista do campo carregada e vazia não há o que definir
  // para o evento todo, e o padrão vira `traveler_rules`.
  const costCenterEventAllowed = options.debitCompanyOptions?.costCenters.length !== 0;
  const motiveEventAllowed = options.workspaceOptions?.motives.length !== 0;
  const costCenterPath = pathOf(chosenCostCenterPath, costCenterEventAllowed);
  const motivePath = pathOf(chosenMotivePath, motiveEventAllowed);

  const create = useMutation({
    mutationFn: (refs: Corporate) =>
      miceRepository.createProject(
        orgId,
        toCreateRequest({ name, eventStart, eventEnd }, refs, corporateProject),
      ),
    onSuccess: async (project) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mice.projects(orgId) });
      fireAndForget(
        navigate({
          to: '/orgs/$orgSlug/mice/$projectId',
          params: { orgSlug, projectId: project.id },
        }),
      );
    },
  });

  // Cada escolha invalida as que dependem dela: o que vinha do workspace (ou da empresa de
  // débito) anterior não vale para o novo.
  const resetDebitCompany = (value: Ref): void => {
    setChosenDebitCompany(value);
    setChosenCostCenter(null);
    setChosenCostCenterPath(null);
    setCorporateProject(null);
  };
  const resetWorkspace = (value: Ref): void => {
    setWorkspace(value);
    resetDebitCompany(null);
    setChosenMotive(null);
    setChosenMotivePath(null);
    setRequester(null);
  };

  const event = { name, eventStart, eventEnd };
  const corporate = corporateOf({
    client,
    workspace,
    debitCompany,
    costCenter,
    motive,
    costCenterPath,
    motivePath,
    requester,
  });

  return {
    name,
    setName,
    eventStart,
    setEventStart,
    eventEnd,
    setEventEnd,
    rangeInvalid: isRangeInvalid(event),
    eventComplete: isEventComplete(event),
    clientSearch: {
      queryKey: (term) => queryKeys.mice.clients(orgId, term),
      search: (term) => miceRepository.searchClients(orgId, term),
    },
    requesterSearch: {
      queryKey: (term) => queryKeys.mice.requesters(orgId, clientId, workspaceId, term),
      search: (term) => miceRepository.searchRequesters(orgId, workspaceId, clientId, term),
    },
    client,
    setClient: (value) => {
      setClient(value);
      // Grupo com um só workspace MICE: não há o que escolher, entra como se o usuário o tivesse
      // escolhido.
      resetWorkspace(value?.workspaces.length === 1 ? (value.workspaces[0] ?? null) : null);
    },
    workspace,
    setWorkspace: resetWorkspace,
    ...options,
    setDebitCompany: resetDebitCompany,
    setCostCenter: setChosenCostCenter,
    costCenterPath,
    setCostCenterPath: setChosenCostCenterPath,
    costCenterEventAllowed,
    setMotive: setChosenMotive,
    motivePath,
    setMotivePath: setChosenMotivePath,
    motiveEventAllowed,
    corporateProject,
    setCorporateProject,
    requester,
    setRequester,
    canSubmit: isEventComplete(event) && corporate !== null && !create.isPending,
    submit: () => {
      if (corporate) create.mutate(corporate);
    },
  };
}
